import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { X, Minus, Plus, ShoppingBag } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useStore } from "@/context/StoreContext";
import ProductImage from "@/components/ProductImage";
import { THUMB_IMAGE_SIZES } from "@/lib/images";
import { inr } from "@/lib/format";

const CartDrawer = () => {
  const navigate = useNavigate();
  const { cart, cartOpen, setCartOpen, updateQty, removeFromCart, subtotal } = useStore();

  const checkout = () => {
    setCartOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 bg-background border-l border-border flex flex-col [&>button.absolute]:hidden"
        data-testid="cart-drawer"
      >
        <SheetTitle className="sr-only">Your shopping bag</SheetTitle>
        <SheetDescription className="sr-only">List of items in your shopping bag with options to update quantity or proceed to checkout.</SheetDescription>
        <div className="flex items-center justify-between px-6 py-5 border-b border-border">
          <div>
            <p className="overline">Your Bag</p>
            <h2 className="serif text-2xl mt-1">{cart.length} {cart.length === 1 ? "piece" : "pieces"}</h2>
          </div>
          <button onClick={() => setCartOpen(false)} className="min-h-11 min-w-11 inline-flex items-center justify-center" data-testid="cart-close" aria-label="Close bag">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {cart.length === 0 ? (
            <div className="text-center py-20" data-testid="cart-empty">
              <ShoppingBag className="w-8 h-8 mx-auto text-secondary" />
              <p className="serif text-2xl mt-4">Your bag is empty</p>
              <p className="text-sm text-secondary mt-2">Begin a new edit.</p>
              <button
                onClick={() => { setCartOpen(false); navigate("/"); }}
                className="btn-outline mt-6"
                data-testid="cart-shop-now"
              >
                Discover
              </button>
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {cart.map((item) => (
                <li key={item.key} className="py-5 flex gap-4" data-testid={`cart-item-${item.product_id}`}>
                  <Link to={`/product/${item.product_id}`} onClick={() => setCartOpen(false)} className="shrink-0">
                    <ProductImage src={item.image} alt={item.name} sizes={THUMB_IMAGE_SIZES} className="w-20 h-28 object-cover" />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-2">
                      <div>
                        <p className="serif text-base leading-tight">{item.name}</p>
                        <p className="text-xs text-secondary mt-1">Size {item.size} · {item.color}</p>
                      </div>
                      <button
                        onClick={() => removeFromCart(item.key)}
                        className="text-xs uppercase tracking-[0.22em] text-secondary hover:text-primary"
                        data-testid={`cart-remove-${item.product_id}`}
                      >
                        Remove
                      </button>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="inline-flex items-center border border-border">
                        <button
                          onClick={() => updateQty(item.key, item.quantity - 1)}
                          className="min-h-11 min-w-11 inline-flex items-center justify-center"
                          data-testid={`cart-dec-${item.product_id}`}
                          aria-label="Decrease quantity"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="px-3 text-sm min-w-[2rem] text-center" data-testid={`cart-qty-${item.product_id}`}>{item.quantity}</span>
                        <button
                          onClick={() => updateQty(item.key, item.quantity + 1)}
                          className="min-h-11 min-w-11 inline-flex items-center justify-center"
                          data-testid={`cart-inc-${item.product_id}`}
                          aria-label="Increase quantity"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-sm font-medium">{inr(item.price * item.quantity)}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {cart.length > 0 && (
          <div className="border-t border-border px-6 py-5 space-y-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-secondary">Subtotal</span>
              <span className="font-medium" data-testid="cart-subtotal">{inr(subtotal)}</span>
            </div>
            <p className="text-xs text-secondary">Shipping & taxes calculated at checkout.</p>
            <button onClick={checkout} className="btn-primary w-full" data-testid="cart-checkout-btn">
              Proceed to Checkout
            </button>
            <button
              onClick={() => { setCartOpen(false); navigate("/cart"); }}
              className="w-full min-h-11 text-xs uppercase tracking-[0.22em] hover:opacity-70 transition"
              data-testid="cart-view-bag"
            >
              View Full Bag
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
